import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DollarSign, ShoppingBag, Users, Palette, RefreshCw, Sparkles, Upload, FileText, Mail, Trash2, Archive, ArchiveRestore, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, UserCog, Search, Shield, Receipt, FileDown, Edit3, Plus, Package, Send, Reply, X, Flag, ShieldCheck, ShieldAlert, ShieldX, Crown, Award, Gift, Minus, Power, PowerOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import RichEditor from '@/components/RichEditor';
import { useCurrency } from '@/context/CurrencyContext';
import { useConfirm } from '@/context/ConfirmContext';
import Pagination from '@/components/Pagination';
import BulkActionBar from '@/components/BulkActionBar';
import { useSelection } from '@/lib/useSelection';
import { openInvoice } from '@/lib/invoice';
import { Kpi } from './_shared';

export const PagesSection = () => {
  const confirm = useConfirm();
  const [legals, setLegals] = useState([]);
  const [editingSlug, setEditingSlug] = useState('terms');
  const [draft, setDraft] = useState({ title: '', content: '' });
  const [newOpen, setNewOpen] = useState(false);
  const [newPage, setNewPage] = useState({ slug: '', title: '', content: '' });

  const load = async () => {
    const { data } = await api.get('/admin/legal');
    setLegals(data);
    const cur = data.find(l => l.slug === editingSlug) || data[0];
    if (cur) { setDraft({ title: cur.title, content: cur.content }); setEditingSlug(cur.slug); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const select = (slug) => {
    setEditingSlug(slug);
    const cur = legals.find(l => l.slug === slug);
    if (cur) setDraft({ title: cur.title, content: cur.content });
  };
  const save = async () => {
    try { await api.patch(`/admin/legal/${editingSlug}`, draft); toast.success('Saved'); load(); }
    catch { toast.error('Save failed'); }
  };
  const createPage = async () => {
    if (!newPage.slug || !newPage.title) { toast.error('Enter slug and title'); return; }
    try {
      const { data } = await api.post('/admin/legal', newPage);
      toast.success('Page created');
      setNewOpen(false);
      setNewPage({ slug: '', title: '', content: '' });
      await load();
      setEditingSlug(data.slug);
      setDraft({ title: data.title, content: data.content });
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
  };
  const removePage = async (slug) => {
    const ok = await confirm({ title: 'Delete this page?', description: 'This permanently removes the page from your site.', confirmLabel: 'Delete', variant: 'danger' });
    if (!ok) return;
    try { await api.delete(`/admin/legal/${slug}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const current = legals.find(l => l.slug === editingSlug);

  return (
    <div className="space-y-4">
      <Card className="jw-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-4 h-4 text-orange-400" />
          <h3 className="font-semibold">Site Pages</h3>
          <Button onClick={() => setNewOpen(true)} size="sm" data-testid="admin-legal-new-button" className="jw-btn-gloss-ghost ml-auto h-8"><Plus className="w-4 h-4 mr-1" /> New page</Button>
        </div>
        <div className="flex gap-2 flex-wrap mb-4">
          {legals.map(l => (
            <div key={l.slug} className="flex items-center">
              <button onClick={() => select(l.slug)} data-testid={`admin-legal-${l.slug}-button`} className={`px-3 py-1.5 rounded-l-md text-sm border ${editingSlug === l.slug ? 'jw-btn-primary border-transparent' : 'jw-btn-secondary'}`}>{l.title}</button>
              {l.custom && (
                <button onClick={() => removePage(l.slug)} data-testid={`admin-legal-${l.slug}-delete`} title="Delete page" className={`px-2 py-1.5 rounded-r-md border-l-0 text-rose-400 border ${editingSlug === l.slug ? 'jw-btn-primary border-transparent' : 'jw-btn-secondary'}`}><X className="w-3.5 h-3.5" /></button>
              )}
            </div>
          ))}
        </div>
        {current && (
          <div className="space-y-3">
            <div>
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Page URL</label>
              <p className="text-xs" style={{ color: 'var(--jw-text-subtle)' }}>/legal/{current.slug}</p>
            </div>
            <div>
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Title</label>
              <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="admin-legal-title-input" />
            </div>
            <div>
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Content</label>
              <RichEditor value={draft.content} onChange={(html) => setDraft({ ...draft, content: html })} placeholder="Write your page content..." testId="admin-legal-content" />
            </div>
            <div className="flex justify-end">
              <Button onClick={save} data-testid="admin-legal-save-button" className="jw-btn-glossy">Save changes</Button>
            </div>
          </div>
        )}
      </Card>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="admin-legal-new-dialog">
          <DialogHeader><DialogTitle>Create a new page</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Slug (URL)</label>
              <Input value={newPage.slug} onChange={(e) => setNewPage({ ...newPage, slug: e.target.value.replace(/[^a-z0-9-]/gi, '-').toLowerCase() })} placeholder="about-us" data-testid="admin-legal-new-slug-input" /></div>
            <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Title</label>
              <Input value={newPage.title} onChange={(e) => setNewPage({ ...newPage, title: e.target.value })} placeholder="About Us" data-testid="admin-legal-new-title-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button onClick={createPage} data-testid="admin-legal-new-create-button" className="jw-btn-glossy">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PagesSection;
